import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Star, Pencil, Trash2, MessageSquare, BadgeCheck } from "lucide-react";
import { toast } from "sonner";

function Stars({ rating, onChange = null, size = "w-5 h-5" }) {
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((n) => {
        const filled = n <= rating;
        const cls = `${size} ${filled ? "fill-terracotta text-terracotta" : "text-edge"}`;
        return onChange ? (
          <button
            key={n}
            type="button"
            data-testid={`star-${n}`}
            onClick={() => onChange(n)}
            className="hover:scale-110 transition-transform"
            aria-label={`${n} star${n === 1 ? "" : "s"}`}
          >
            <Star className={cls} strokeWidth={1.5} />
          </button>
        ) : (
          <Star key={n} className={cls} strokeWidth={1.5} />
        );
      })}
    </div>
  );
}


export default function BookReviews({ bookId, owned }) {
  const { user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [mine, setMine] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get(`/books/${bookId}/reviews`).then((r) => setReviews(r.data || [])).catch(() => {}),
      owned && user ? api.get(`/books/${bookId}/reviews/mine`).then((r) => setMine(r.data.review)).catch(() => {}) : Promise.resolve(),
    ]).finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [bookId, owned, user?.id]);

  const openEditor = () => {
    if (mine) {
      setRating(mine.rating);
      setComment(mine.comment || "");
    } else {
      setRating(5);
      setComment("");
    }
    setEditing(true);
  };

  const save = async () => {
    setSubmitting(true);
    try {
      if (mine) {
        await api.patch(`/books/${bookId}/reviews/mine`, { rating, comment });
        toast.success("Review updated");
      } else {
        await api.post(`/books/${bookId}/reviews`, { rating, comment });
        toast.success("Thanks for your review");
      }
      setEditing(false);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not save review";
      toast.error(typeof detail === "string" ? detail : "Could not save review");
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Remove your review?")) return;
    try {
      await api.delete(`/books/${bookId}/reviews/mine`);
      setMine(null);
      toast("Review removed");
      load();
    } catch {
      toast.error("Could not remove");
    }
  };

  return (
    <section id="reviews" data-testid="reviews-section" className="mt-16 border-t border-edge pt-12 scroll-mt-28">
      <div className="flex items-baseline justify-between">
        <div>
          <div className="label-eyebrow flex items-center gap-2"><MessageSquare className="w-3.5 h-3.5" /> Reader reviews</div>
          <h3 className="font-serif text-3xl mt-2">
            {reviews.length === 0 ? "Be the first to review." : `${reviews.length} review${reviews.length === 1 ? "" : "s"}`}
          </h3>
        </div>
        {owned && !editing && (
          <button
            data-testid={mine ? "edit-review-btn" : "write-review-btn"}
            onClick={openEditor}
            className="btn-secondary"
          >
            <Pencil className="w-3.5 h-3.5 mr-2" /> {mine ? "Edit yours" : "Write a review"}
          </button>
        )}
      </div>

      {editing && (
        <div data-testid="review-editor" className="mt-6 border border-edge bg-linen p-6 space-y-4">
          <div>
            <label className="label-eyebrow block mb-2">Your rating</label>
            <Stars rating={rating} onChange={setRating} size="w-7 h-7" />
          </div>
          <div>
            <label className="label-eyebrow block mb-1">A short comment (optional)</label>
            <textarea
              data-testid="review-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={4}
              placeholder="What did you love (or wrestle with)?"
              className="w-full border border-edge bg-white px-3 py-2 text-sm focus:outline-none focus:border-ink resize-none"
            />
          </div>
          <div className="flex justify-between items-center border-t border-edge pt-4">
            {mine ? (
              <button onClick={remove} data-testid="delete-review-btn" className="text-xs uppercase tracking-widest text-muted_ink hover:text-terracotta inline-flex items-center gap-1">
                <Trash2 className="w-3 h-3" /> Remove
              </button>
            ) : <span />}
            <div className="flex gap-2">
              <button onClick={() => setEditing(false)} className="text-xs uppercase tracking-widest px-4 py-2 text-muted_ink hover:text-ink">Cancel</button>
              <button
                data-testid="save-review-btn"
                onClick={save}
                disabled={submitting}
                className="btn-primary disabled:opacity-60"
              >
                {submitting ? "Saving…" : (mine ? "Save changes" : "Post review")}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="mt-8 space-y-6">
        {loading ? (
          <div className="text-muted_ink">Loading…</div>
        ) : reviews.length === 0 ? (
          <div className="text-muted_ink italic">No reviews yet — yours could be the one that helps someone pick this up.</div>
        ) : (
          reviews.map((r) => (
            <div key={r.id} data-testid={`review-${r.id}`} className="border-b border-edge pb-6 last:border-b-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-terracotta/10 text-terracotta font-serif text-lg flex items-center justify-center">
                    {(r.user_name || "R")[0]?.toUpperCase()}
                  </div>
                  <div>
                    <div className="font-serif text-base flex items-center gap-2">
                      {r.user_name}
                      {r.verified_purchase && (
                        <span
                          data-testid={`verified-${r.id}`}
                          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-sage border border-sage/40 bg-sage/5 px-1.5 py-0.5"
                          title="This reader bought the book on Folio"
                        >
                          <BadgeCheck className="w-3 h-3" strokeWidth={2} /> Verified reader
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-muted_ink">{new Date(r.created_at).toLocaleDateString()}</div>
                  </div>
                </div>
                <Stars rating={r.rating} size="w-4 h-4" />
              </div>
              {r.comment && (
                <p className="text-ink mt-3 leading-relaxed italic font-serif">
                  {r.comment}
                </p>
              )}
            </div>
          ))
        )}
      </div>
    </section>
  );
}
